import type { UserSettings } from '@pn/core/domain/settings';
import type { ISODateTimeString } from '@pn/core/domain/types';
import { IntelPlans, UserPlans } from '@pn/core/domain/user';
import { hasKey } from '@pn/core/utils/logic';
import type { ApiUserEnterprise } from '@pn/services/api/enterprise/types';
import { isObject } from 'lodash-es';

export type ApiIncompleteUser = {
  id: string;
  email: string;
  enterprise: ApiUserEnterprise | null;
  user_plan?: UserPlans;
};

export function isApiIncompleteUser(arg: unknown): arg is ApiIncompleteUser {
  return isObject(arg) && !hasKey(arg, 'id');
}

export type ApiUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  user_plan: UserPlans; // deprecated
  intel_plan: IntelPlans; // deprecated
  plan: {
    maps: UserPlans;
    intel: IntelPlans;
  };
  has_gpt_access?: boolean;
  enterprise: ApiUserEnterprise | null;
  connection: string | null;
  company: string | null;
  title: string | null;
  home_base_lat: number | null;
  home_base_lng: number | null;
  created_at: ISODateTimeString;
  gpt_usage: {
    free_credits_remaining: number;
    paid_credits_balance: number;
    free_credits_reset_at: ISODateTimeString;
  };
  stripe_account: {
    id: string;
    email: string;
    createdAt: ISODateTimeString;
    delinquent: boolean;
    currency: string;
  } | null;
  is_admin: boolean;
  user_config?: UserSettings;
};

export function isApiUser(arg: unknown): arg is ApiUser {
  return (
    isObject(arg) &&
    hasKey(arg, 'id') &&
    hasKey(arg, 'email') &&
    hasKey(arg, 'first_name') &&
    hasKey(arg, 'last_name') &&
    hasKey(arg, 'user_plan') &&
    hasKey(arg, 'connection') &&
    hasKey(arg, 'company') &&
    hasKey(arg, 'title')
  );
}

export type ApiTargetUser = Partial<ApiUser> & {
  enterprise_id: string | number | undefined;
};

export type ApiEnterpriseUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  user_plan: UserPlans;
  connection: string | null;
};

export function isApiEnterpriseUser(arg: unknown): arg is ApiEnterpriseUser {
  return (
    isObject(arg) &&
    hasKey(arg, 'id') &&
    hasKey(arg, 'email') &&
    hasKey(arg, 'first_name') &&
    hasKey(arg, 'last_name') &&
    hasKey(arg, 'user_plan') &&
    hasKey(arg, 'connection')
  );
}

export type ApiEnterpriseInvitedUser = Pick<
  ApiUser,
  'id' | 'email' | 'user_plan' | 'enterprise' | 'created_at'
>;

export function isApiEnterpriseInvitedUser(
  arg: unknown
): arg is ApiEnterpriseInvitedUser {
  return (
    isObject(arg) &&
    hasKey(arg, 'id') &&
    hasKey(arg, 'email') &&
    hasKey(arg, 'user_plan')
  );
}

/* Used in projects, lists, layers, and annotations. */
export type ApiProjectUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
};

export type ApiInviteDetails = {
  email: string;
  user_exists: boolean;
  user: {
    first_name: string;
    last_name: string;
  };
  created_at: string;
  enterprise: {
    id: string;
    name: string;
    logo: string;
    auth_connections: string[];
  };
};

export type ApiCheckoutSession = {
  amount: number;
  url: string;
};
